import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Loader2, Plus, Save, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { SortableList } from "@/components/admin/SortableList";

const CityCard = ({ city, onRemove, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [hotels, setHotels] = useState(city.hotels);
  const [busy, setBusy] = useState(false);
  const dirty = hotels.map((h) => h.id).join() !== city.hotels.map((h) => h.id).join();

  useEffect(() => {
    setHotels(city.hotels);
  }, [city.hotels]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/cities/${city.id}/order`, { hotel_ids: hotels.map((h) => h.id) });
      toast.success(`${city.name} order saved`);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-stone-200 rounded-lg" data-testid={`city-card-${city.slug}`}>
      <div className="flex items-center gap-3 px-4 py-3">
        <button onClick={() => setOpen((o) => !o)} className="flex-1 flex items-center gap-2 text-left" data-testid={`city-toggle-${city.slug}`}>
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? "" : "-rotate-90"}`} />
          <span className="font-bold text-sm uppercase">{city.name}</span>
          <span className="text-xs text-stone-500">{city.hotels.length} listing(s)</span>
        </button>
        <button onClick={() => onRemove(city)} className="p-1.5 text-red-500 hover:bg-red-50 rounded" data-testid={`city-remove-${city.slug}`} aria-label="Remove city"><Trash2 className="w-4 h-4" /></button>
      </div>
      {open && (
        <div className="px-4 pb-4 border-t border-stone-100 pt-3">
          {hotels.length ? (
            <SortableList items={hotels} onChange={setHotels} testid={`city-hotels-${city.slug}`} render={(h) => <span className="text-sm">{h.name}</span>} />
          ) : <p className="text-sm text-stone-400">No hotels in this city.</p>}
          {dirty && (
            <button onClick={save} disabled={busy} className="mt-3 inline-flex items-center gap-1.5 bg-crimson text-white text-sm font-bold rounded-md px-4 py-2" data-testid={`city-save-order-${city.slug}`}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Save order
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default function Cities() {
  const { reload } = useSite();
  const [cities, setCities] = useState([]);
  const [name, setName] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/directory");
    setCities(data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const refresh = () => { load(); reload(); };

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await api.post("/admin/cities", { name });
      toast.success("City added");
      setName("");
      refresh();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const remove = async (city) => {
    if (city.hotels.length) return toast.error(`Cannot remove ${city.name}: it has ${city.hotels.length} listing(s). Move or delete them first.`);
    if (!window.confirm(`Remove city "${city.name}"?`)) return;
    try {
      await api.delete(`/admin/cities/${city.id}`);
      toast.success("City removed");
      refresh();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div data-testid="admin-cities">
      <h1 className="text-3xl font-bold">Cities</h1>
      <p className="text-sm text-stone-500 mt-1 mb-6">{cities.length} cities, shown A–Z. Expand a city to reorder its hotels (drag or arrows), then Save order.</p>
      <form onSubmit={add} className="flex gap-2 mb-6 max-w-lg">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Add a new city" className="flex-1 border border-stone-300 bg-white rounded-md px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-crimson/30" data-testid="city-add-input" />
        <button className="inline-flex items-center gap-1.5 bg-crimson hover:bg-crimson-hover text-white rounded-md px-4 text-sm font-bold" data-testid="city-add-btn"><Plus className="w-4 h-4" />Add City</button>
      </form>
      <div className="grid md:grid-cols-2 gap-3 items-start">
        {cities.map((c) => <CityCard key={c.id} city={c} onRemove={remove} onSaved={refresh} />)}
      </div>
    </div>
  );
}
